import { ChangeDetectionStrategy, Component, Input, inject, output } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { AuthorizationService } from '@services'
import { FeatureIdentifiersComponent } from './feature-identifiers.component'
import { CommonModule } from '@angular/common'
import { RouterModule } from '@angular/router'
import { IconsModule } from '@ziptility/icons'
import { MatMenuModule } from '@angular/material/menu'
import { IBvFeature } from '@ziptility/modules'

@Component({
  selector: 'panel-header',
  template: `
    <mat-menu #featureMenu="matMenu" xPosition="after">
      <div class="bv-menu">
        @if (canEditFeature) {
          @if (!feature.geometry.coordinates.length) {
            <div class="menu-item" mat-menu-item (click)="onMove.emit()">Add to map</div>
          }
          <div class="menu-item" mat-menu-item (click)="showFeatureIdentifiers()">Rename</div>
        }
        @if (this.authorizationService.canExportAllLayers()) {
          <div class="menu-item" mat-menu-item [matMenuTriggerFor]="exportMenu">Export</div>
        }

        <mat-menu #exportMenu="matMenu" xPosition="after">
          <div class="bv-menu">
            <div class="menu-item" mat-menu-item (click)="onExport.emit('shapefile')">to Shapefile</div>
            <div class="menu-item" mat-menu-item (click)="onExport.emit('csv')">to Excel</div>
          </div>
        </mat-menu>

        @if (canEditFeature) {
          <div class="menu-item warning" mat-menu-item (click)="onDelete.emit()">Delete</div>
        }
      </div>
    </mat-menu>

    <div class="bv-panel-header">
      <div class="grid grid-cols-12 shadow-lg">
        <div class="col-span-1 p-4 place-self-center items-center cursor-pointer" (click)="onClose.emit()">
          <i-feather name="x"></i-feather>
        </div>
        <div class="col-span-10 text-center p-4 font-semibold">Asset Profile</div>
        <div
          class="col-span-1 justify-center items-center p-4 place-self-center cursor-pointer"
          [mat-menu-trigger-for]="featureMenu"
        >
          <i-feather name="more-horizontal" class="self-end"></i-feather>
        </div>
      </div>

      @if (systemId) {
        <div class="relative w-full overflow-hidden" style="height: 200px">
          @if (mapPreviewUrl) {
            <div
              [ngStyle]="{ 'background-image': mapPreviewUrl }"
              class="absolute w-full h-full bg-center bg-cover bg-no-repeat"
            ></div>
          }

          <div class="bg-gradient-to-t from-black to-transparent pt-5 pb-0 px-8 absolute left-0 bottom-0 z-50 w-full">
            <div class="title-row text-white mb-4">
              <div class="">
                <button
                  class="font-semibold text-xl mb-1"
                  (click)="showFeatureIdentifiers()"
                  [disabled]="!canEditFeature"
                >
                  {{ layerTitle }} {{ systemId }}
                </button>
                <div class="text-sm flex flex-row items-center">
                  <button
                    class="text-gray-300 flex items-center"
                    [disabled]="!canEditFeature"
                    (click)="showFeatureIdentifiers()"
                  >
                    @if (!locationIdentifier) {
                      <i-feather name="plus"></i-feather>&nbsp; Location nickname
                    } @else {
                      {{ locationIdentifier }}
                      <i-feather name="edit-2" class="text-white ml-2"></i-feather>
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="bv-panel-tabs">
          <button
            class="bv-panel-tabs__button"
            routerLink="."
            routerLinkActive="active"
            [routerLinkActiveOptions]="{ exact: true }"
          >
            About
          </button>
          @if (hasDispatch && authorizationService.canViewTasks()) {
            <button class="bv-panel-tabs__button" routerLink="tasks" routerLinkActive="active">
              @if (numOpenTasks) {
                ({{ numOpenTasks }})
              }
              Tasks
            </button>
            <button class="bv-panel-tabs__button" routerLink="routines" routerLinkActive="active">Routines</button>
          }
          @if (hasInventory) {
            <button class="bv-panel-tabs__button" routerLink="products" routerLinkActive="active">Products</button>
          }
          <button class="bv-panel-tabs__button" routerLink="comments" routerLinkActive="active">Notes</button>
          <button class="bv-panel-tabs__button" routerLink="history" routerLinkActive="active">History</button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .bv-panel-tabs {
        display: flex;
        border-top: 1px solid #d8d8d8;
        border-bottom: 1px solid #d8d8d8;
        justify-content: center;
        align-items: center;
      }

      .bv-panel-tabs .bv-panel-tabs__button {
        flex-grow: 1;
        display: inline-block;
        padding: 10px;
        background: #f9fafb;
        font-size: 12px;
        border-bottom: 2px solid transparent;
      }

      .bv-panel-tabs .bv-panel-tabs__button.active {
        border-bottom: 2px solid theme('colors.red.brand');
        font-weight: theme('fontWeight.semibold');
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule, IconsModule, MatMenuModule],
})
export class AssetProfileHeaderComponent {
  readonly authorizationService = inject(AuthorizationService)
  readonly matDialog = inject(MatDialog)

  @Input()
  feature: IBvFeature

  @Input()
  layerTitle: string

  @Input()
  canEditFeature: boolean

  @Input()
  systemId: string | number

  @Input()
  locationIdentifier: string | undefined

  @Input()
  hasDispatch: boolean

  @Input()
  hasInventory: boolean

  @Input()
  mapPreviewUrl: string | undefined

  @Input()
  numOpenTasks: number | undefined

  readonly onDelete = output<void>()

  readonly onMove = output<void>()

  readonly onDuplicate = output<void>()

  readonly onExport = output<string>()

  readonly onClose = output<void>()

  readonly onCopyUrl = output<void>()

  readonly onIdentifiersChange = output<{
    systemId: string
    locationIdentifier: string | undefined
  }>()

  showFeatureIdentifiers() {
    this.matDialog
      .open(FeatureIdentifiersComponent, {
        data: {
          systemId: this.systemId,
          locationIdentifier: this.locationIdentifier,
        },
      })
      .afterClosed()
      .subscribe((result) => result && this.onIdentifiersChange.emit(result))
  }
}
